import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { CacheKeys } from '@/lib/cache'

const ROSTER_KEYS: readonly string[] = [
  CacheKeys.DRIVERS,
  CacheKeys.TRUCKS,
  CacheKeys.TRAILERS,
  CacheKeys.LOSS_RUN_REQUESTS,
]
// Several rosters refetch together after a file or an import; one state fetch covers them.
const COALESCE_MS = 200

/** Refetches the page's checks whenever a roster refetches, so a removed driver's warning goes with them. */
export function useRefreshChecksOnRosterChange(orgId: string) {
  const queryClient = useQueryClient()

  useEffect(() => {
    let timer: number | undefined
    // Each list as last seen: a poll that comes back unchanged keeps its reference and refreshes nothing.
    const seen = new Map<string, unknown>()
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return
      const [key, keyOrgId] = event.query.queryKey as unknown[]
      if (keyOrgId !== orgId || typeof key !== 'string' || !ROSTER_KEYS.includes(key)) return
      if (seen.get(event.query.queryHash) === event.query.state.data) return
      seen.set(event.query.queryHash, event.query.state.data)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: [CacheKeys.FILL_APPLICATION_STATE, orgId] })
      }, COALESCE_MS)
    })
    return () => {
      unsubscribe()
      window.clearTimeout(timer)
    }
  }, [orgId, queryClient])
}
